<template>
  <div class="survey-aggregate">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <font-awesome-icon :icon="['fas', 'spinner']" spin class="text-2xl text-gray-400" />
      <span class="ml-2 text-gray-500">Loading surveys...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <p class="text-red-700">{{ error }}</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="Object.keys(groupedData).length === 0" class="text-center py-12 text-gray-500">
      No surveys found
    </div>

    <!-- Main Content -->
    <div v-else class="flex gap-4">
      <!-- Left Panel: Survey Data -->
      <div :class="showEntityPanel ? 'w-2/3' : 'w-full'" class="space-y-4 transition-all duration-200">
        <div class="flex justify-end">
          <ExportMenu :disabled="!Object.keys(groupedData).length" :exports="[{ type: 'excel', action: exportToExcel }]" />
        </div>
        <div
          v-for="(surveyTypeData, surveyTypeName) in groupedData"
          :key="surveyTypeName"
          class="bg-white rounded-lg border border-gray-200 overflow-hidden"
        >
          <div
            class="bg-gray-100 px-4 py-3 cursor-pointer flex items-center justify-between"
            @click="toggleSurveyType(surveyTypeName)"
          >
            <div class="flex items-center gap-3">
              <font-awesome-icon
                :icon="['fas', expandedSurveyTypes[surveyTypeName] ? 'chevron-down' : 'chevron-right']"
                class="text-gray-500 w-3"
              />
              <h3 class="font-semibold text-gray-900">{{ surveyTypeName }}</h3>
            </div>
            <span class="text-sm text-gray-500">
              {{ surveyTypeData.totalSurveys }} surveys
            </span>
          </div>

          <!-- Questions List -->
          <div v-if="expandedSurveyTypes[surveyTypeName]" class="divide-y divide-gray-100">
            <div
              v-for="question in surveyTypeData.questions"
              :key="question.questionId"
              class="px-4 py-4"
            >
              <div class="flex items-start justify-between mb-3">
                <div class="flex items-start gap-2">
                  <span class="text-xs text-gray-400 font-mono mt-0.5">Q{{ question.questionIndex }}</span>
                  <p class="text-sm font-medium text-gray-800">{{ question.questionText }}</p>
                </div>
                <div class="flex items-center gap-3 ml-4">
                  <span class="text-xs text-gray-500 whitespace-nowrap">
                    {{ question.responsesCount }} responses, {{ question.blankCount }} blank
                  </span>
                </div>
              </div>

              <div class="ml-6 space-y-2">
                <template v-if="question.questionType === 'single_choice' || question.questionType === 'multiple_choice'">
                  <div
                    v-for="(answerData, answerKey) in question.answerDistribution"
                    :key="answerKey"
                    class="flex items-center gap-3"
                    :class="{
                      'cursor-pointer group': answerData.count > 0,
                      'opacity-50': answerData.count === 0,
                      'ring-2 ring-blue-400 rounded': isSelectedAnswer(question.questionId, answerKey)
                    }"
                    @click="selectAnswer(question, answerKey, answerData)"
                  >
                    <div class="flex-1 flex items-center gap-2">
                      <div
                        class="w-full bg-gray-100 rounded-full h-6 relative overflow-hidden transition-all"
                        :class="{ 'group-hover:ring-2 group-hover:ring-blue-300': answerData.count > 0 }"
                      >
                        <div
                          class="h-full rounded-full transition-all duration-300"
                          :class="getBarColor(Object.keys(question.answerDistribution).indexOf(answerKey))"
                          :style="{ width: getBarWidth(answerData.count, question.responsesCount) }"
                        ></div>
                        <div class="absolute inset-0 flex items-center px-3">
                          <span class="text-xs font-semibold text-gray-900 truncate drop-shadow-sm">
                            {{ answerData.label }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="w-20 text-right">
                      <span class="text-sm font-medium text-gray-900">{{ answerData.count }}</span>
                      <span class="text-xs text-gray-500 ml-1">({{ getPercentage(answerData.count, question.responsesCount) }}%)</span>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div
                    class="flex items-center gap-3"
                    :class="question.responsesCount > 0 ? 'cursor-pointer group' : 'opacity-60'"
                    @click="question.responsesCount > 0 && selectTextResponses(question)"
                  >
                    <div class="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden"
                      :class="{
                        'group-hover:ring-2 group-hover:ring-blue-300 transition-all': question.responsesCount > 0,
                        'ring-2 ring-blue-400': isSelectedTextQuestion(question.questionId)
                      }"
                    >
                      <div
                        class="h-full bg-blue-400 rounded-full"
                        :style="{ width: getBarWidth(question.responsesCount, question.responsesCount + question.blankCount) }"
                      ></div>
                      <div class="absolute inset-0 flex items-center px-3">
                        <span class="text-xs font-medium text-gray-700">Text responses</span>
                      </div>
                    </div>
                    <div class="w-20 text-right">
                      <span class="text-sm font-medium text-gray-900">{{ question.responsesCount }}</span>
                      <span class="text-xs text-gray-500 ml-1">({{ question.responseRate }}%)</span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Panel: Entity List -->
      <div
        v-if="showEntityPanel"
        class="w-1/3 bg-white rounded-lg border border-gray-200 flex flex-col max-h-[80vh] sticky top-4"
      >
        <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div class="flex items-start justify-between gap-3">
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-semibold text-gray-900 truncate">{{ panelData.answerLabel }}</h3>
              <p class="text-xs text-gray-500 mt-1 line-clamp-2">{{ panelData.questionText }}</p>
            </div>
            <button
              class="text-gray-400 hover:text-gray-600 p-1 -mr-1 shrink-0"
              @click="closeEntityPanel"
            >
              <font-awesome-icon :icon="['fas', 'times']" class="text-lg" />
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-4">
          <p class="text-xs text-gray-500 mb-3">
            {{ panelData.entities.length }} {{ panelData.entities.length === 1 ? 'entity' : 'entities' }}
          </p>
          <div v-if="panelData.isTextResponse" class="space-y-3">
            <div
              v-for="entity in panelData.entities"
              :key="entity.id"
              class="p-3 bg-gray-50 rounded-lg"
            >
              <a
                :href="`/entity/${entity.id}/`"
                class="sensitive text-sm font-medium text-blue-600 hover:text-blue-800"
                @click.prevent="navigateToEntity(entity.id)"
              >
                {{ entity.name }}
              </a>
              <p class="text-sm text-gray-700 mt-1">{{ entity.response }}</p>
            </div>
          </div>
          <div v-else class="space-y-1">
            <a
              v-for="entity in panelData.entities"
              :key="entity.id"
              :href="`/entity/${entity.id}/`"
              class="flex items-center justify-between p-2 rounded hover:bg-gray-100 group"
              @click.prevent="navigateToEntity(entity.id)"
            >
              <span class="sensitive text-sm text-gray-800 group-hover:text-blue-600 truncate">
                {{ entity.name }}
              </span>
              <font-awesome-icon
                :icon="['fas', 'chevron-right']"
                class="text-xs text-gray-300 group-hover:text-blue-400 shrink-0 ml-2"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSpinner, faChevronDown, faChevronRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { exportToExcel } from '@/utils/exportArraytoXlsx';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'

library.add(faSpinner, faChevronDown, faChevronRight, faTimes);

const BAR_COLORS = [
  'bg-blue-400', 'bg-green-400', 'bg-amber-400', 'bg-purple-400',
  'bg-pink-400', 'bg-cyan-400', 'bg-orange-400', 'bg-teal-400',
];

export default {
  name: 'SurveyAggregate',
  components: {
    FontAwesomeIcon,
    ExportMenu,
  },
  data() {
    return {
      loading: true,
      error: null,
      surveys: [],
      groupedData: {},
      expandedSurveyTypes: {},
      showEntityPanel: false,
      selectedQuestionId: null,
      selectedAnswerKey: null,
      panelData: {
        questionText: '',
        answerLabel: '',
        entities: [],
        isTextResponse: false
      }
    };
  },
  mounted() {
    this.fetchSurveys();
  },
  methods: {
    async fetchSurveys() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get('/api/surveys/');
        this.surveys = response.data || [];

        // Process and group the data
        this.groupedData = this.processData(this.surveys);

        // Start with all survey types collapsed
        for (const typeName of Object.keys(this.groupedData)) {
          this.expandedSurveyTypes[typeName] = false;
        }
      } catch (err) {
        console.error('Error fetching surveys:', err);
        this.error = 'Failed to load surveys. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    processData(surveys) {
      const grouped = {};

      for (const survey of surveys) {
        const typeName = survey.survey_type_name || 'Unknown Survey Type';
        const entityInfo = {
          id: survey.entity_id,
          name: survey.related_object_name || `Entity ${survey.entity_id}`
        };

        if (!grouped[typeName]) {
          grouped[typeName] = {
            totalSurveys: 0,
            questionsMap: {}
          };
        }

        grouped[typeName].totalSurveys++;

        if (!Array.isArray(survey.answers)) continue;

        for (const answer of survey.answers) {
          const questionId = answer.survey_question;

          if (!grouped[typeName].questionsMap[questionId]) {
            grouped[typeName].questionsMap[questionId] = {
              questionId,
              questionIndex: answer.survey_question_index,
              questionText: answer.survey_question_name,
              questionType: answer.survey_question_type || 'text',
              questionChoices: answer.survey_question_choices || {},
              responsesCount: 0,
              blankCount: 0,
              answerData: {},
              textResponses: []
            };
          }

          const questionData = grouped[typeName].questionsMap[questionId];
          const hasResponse = answer.response?.trim();

          if (hasResponse) {
            questionData.responsesCount++;
            const isChoice = ['single_choice', 'multiple_choice'].includes(questionData.questionType);
            if (isChoice) {
              this.trackAnswerEntities(questionData, answer.response, entityInfo);
            } else {
              this.trackTextResponseEntity(questionData, entityInfo, answer.response);
            }
          } else {
            questionData.blankCount++;
          }
        }
      }

      for (const typeName in grouped) {
        const questions = Object.values(grouped[typeName].questionsMap)
          .sort((a, b) => (a.questionIndex || 0) - (b.questionIndex || 0));

        for (const q of questions) {
          const total = q.responsesCount + q.blankCount;
          q.responseRate = total ? Math.round((q.responsesCount / total) * 100) : 0;
          q.answerDistribution = this.buildAnswerDistribution(q);
        }

        grouped[typeName].questions = questions;
        delete grouped[typeName].questionsMap;
      }

      return grouped;
    },

    toggleSurveyType(typeName) {
      this.expandedSurveyTypes[typeName] = !this.expandedSurveyTypes[typeName];
    },

    async exportToExcel() {
      const rows = [];

      for (const survey of this.surveys) {
        const entityId = survey.entity_id;
        if (!Array.isArray(survey.answers)) continue;

        for (const answer of survey.answers) {
          let responseValue = answer.response || '';
          if (responseValue && ['single_choice', 'multiple_choice'].includes(answer.survey_question_type)) {
            try { responseValue = Object.values(JSON.parse(responseValue)).join(', '); } catch { /* keep original */ }
          }

          rows.push({
            'Survey Type': survey.survey_type_name || 'Unknown',
            'Survey ID': survey.id,
            'Entity ID': entityId,
            'Entity Name': survey.related_object_name || `Entity ${entityId}`,
            'Question Index': answer.survey_question_index,
            'Question': answer.survey_question_name,
            'Question Type': answer.survey_question_type || 'text',
            'Response': responseValue,
            'Response (Raw)': answer.response || '',
            'Survey Created': survey.created_at,
            'Survey Updated': survey.updated_at
          });
        }
      }

      await exportToExcel(rows, 'Survey_Responses_Raw.xlsx');
    },

    trackTextResponseEntity(questionData, entityInfo, responseText) {
      if (!questionData.textResponses.some(r => r.entity.id === entityInfo.id)) {
        questionData.textResponses.push({
          entity: { ...entityInfo },
          response: responseText
        });
      }
    },

    trackAnswerEntities(questionData, responseStr, entityInfo) {
      let keys = [];
      try {
        keys = Object.keys(JSON.parse(responseStr));
      } catch {
        const trimmed = responseStr.trim();
        if (trimmed) keys = [trimmed];
      }

      for (const key of keys) {
        if (!questionData.answerData[key]) {
          questionData.answerData[key] = { count: 0, entities: [] };
        }
        questionData.answerData[key].count++;
        if (!questionData.answerData[key].entities.some(e => e.id === entityInfo.id)) {
          questionData.answerData[key].entities.push(entityInfo);
        }
      }
    },

    buildAnswerDistribution(question) {
      const distribution = {};
      const choices = question.questionChoices || {};

      for (const [key, label] of Object.entries(choices)) {
        distribution[key] = { label, count: 0, entities: [] };
      }

      for (const [key, data] of Object.entries(question.answerData)) {
        if (distribution[key]) {
          distribution[key].count = data.count;
          distribution[key].entities = data.entities;
        } else {
          distribution[key] = { label: key, count: data.count, entities: data.entities };
        }
      }

      const sorted = Object.entries(distribution).sort((a, b) => b[1].count - a[1].count);
      return Object.fromEntries(sorted);
    },

    selectAnswer(question, answerKey, answerData) {
      if (answerData.count === 0) return;

      if (this.selectedQuestionId === question.questionId && this.selectedAnswerKey === answerKey) {
        return this.closeEntityPanel();
      }

      this.selectedQuestionId = question.questionId;
      this.selectedAnswerKey = answerKey;
      this.panelData = {
        questionText: question.questionText,
        answerLabel: answerData.label,
        entities: (answerData.entities || []).map(e => ({ ...e })),
        isTextResponse: false
      };
      this.showEntityPanel = true;
    },

    selectTextResponses(question) {
      if (question.responsesCount === 0) return;

      if (this.selectedQuestionId === question.questionId && this.selectedAnswerKey === '_text') {
        return this.closeEntityPanel();
      }

      this.selectedQuestionId = question.questionId;
      this.selectedAnswerKey = '_text';
      this.panelData = {
        questionText: question.questionText,
        answerLabel: 'Text Responses',
        entities: (question.textResponses || []).map(r => ({ ...r.entity, response: r.response })),
        isTextResponse: true
      };
      this.showEntityPanel = true;
    },

    isSelectedAnswer(questionId, answerKey) {
      return this.showEntityPanel && this.selectedQuestionId === questionId && this.selectedAnswerKey === answerKey;
    },

    isSelectedTextQuestion(questionId) {
      return this.showEntityPanel && this.selectedQuestionId === questionId && this.selectedAnswerKey === '_text';
    },

    closeEntityPanel() {
      this.showEntityPanel = false;
      this.selectedQuestionId = null;
      this.selectedAnswerKey = null;
    },

    navigateToEntity(entityId) {
      this.$router.push(`/entity/${entityId}/`);
    },

    getBarWidth(count, total) {
      if (!total) return '0%';
      return `${Math.max((count / total) * 100, 2)}%`;
    },

    getPercentage(count, total) {
      return total ? Math.round((count / total) * 100) : 0;
    },

    getBarColor(index) {
      return BAR_COLORS[index % BAR_COLORS.length];
    }
  }
};
</script>

<style scoped>
@reference "../../assets/theme.css";
.survey-aggregate {
  @apply w-full;
}

/* Line clamp utility for question text */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
